// Per-sound mix within the allowed ranges (SPEC §3.4): volume, pan, low-pass cutoff, reverb and
// delay sends, and two insert effects (a drive, then a filter, each with an amount). Each knob
// gesture is one undo step. Master and FX are fixed for the day.
import { useRef } from "react";
import {
  defaultMix,
  DRIVE_KINDS,
  INSERT_FILTER_KINDS,
  INSTRUMENT_ROLES,
  NUM_SOUNDS,
  ROLE_LABEL,
  laneSlot,
  loopSound,
  MIX_RANGE,
  type DriveKind,
  type InsertFilterKind,
  type Inserts,
  type SoundMix,
  isAtmosPad,
  FIRST_ATMOS,
} from "@eb/shared";
import { Knob } from "../ui/Knob";
import { setMix } from "./ops";
import { beginGesture, edit, endGesture, useEditor } from "./store";

const pct = (v: number) => `${Math.round(v * 100)}%`;
const hz = (v: number) => (v >= 19999 ? "open" : v >= 1000 ? `${(v / 1000).toFixed(1)} kHz` : `${Math.round(v)} Hz`);
const pan = (v: number) => (Math.abs(v) < 0.02 ? "C" : v < 0 ? `L${Math.round(-v * 100)}` : `R${Math.round(v * 100)}`);

export function MixPanel() {
  const spec = useEditor((s) => s.spec);
  const slot = useEditor((s) => s.selectedSlot);
  const mix = useEditor((s) => s.hist.present.mix[slot]);
  const variants = useEditor((s) => s.hist.present.variants);
  const lanes = useEditor((s) => s.hist.present.lanes);
  const open = useRef(false);
  if (!spec) return null;
  // Every sound, extra lanes (20–27) included.
  const defaults = defaultMix(spec);
  const m: SoundMix = mix ?? defaults[slot];
  const change = (patch: Partial<SoundMix>) => {
    if (!open.current) {
      beginGesture("Mix");
      open.current = true;
    }
    edit((l) => setMix(l, slot, patch, defaults));
  };
  const done = () => {
    if (open.current) endGesture();
    open.current = false;
  };
  const ins: Inserts = m.inserts ?? { drive: "off", drive_amount: 0.5, filter: "off", filter_amount: 0.5 };
  const setInserts = (patch: Partial<Inserts>, oneStep: boolean) => {
    if (oneStep) {
      beginGesture("Insert");
      edit((l) => setMix(l, slot, { inserts: { ...ins, ...patch } }, defaults));
      endGesture();
    } else change({ inserts: { ...ins, ...patch } });
  };
  const amountKnob = (k: "drive_amount" | "filter_amount", label: string) => (
    <Knob
      label={label}
      value={ins[k]}
      min={0}
      max={1}
      defaultValue={0.5}
      format={pct}
      onChange={(v) => setInserts({ [k]: v }, false)}
      onChangeEnd={done}
      testId={`knob-${k}`}
      size={46}
    />
  );
  const knob = (
    k: keyof typeof MIX_RANGE,
    label: string,
    format: (v: number) => string,
    extra: Partial<{ bipolar: boolean; log: boolean }> = {},
  ) => (
    <Knob
      label={label}
      value={m[k]}
      min={MIX_RANGE[k][0]}
      max={MIX_RANGE[k][1]}
      defaultValue={defaults[slot][k]}
      format={format}
      onChange={(v) => change({ [k]: v })}
      onChangeEnd={done}
      testId={`knob-${k}`}
      size={46}
      {...extra}
    />
  );
  return (
    <section className="mix-panel panel" aria-label="Mix">
      <div className="row mix-head">
        <label>
          <span className="sr-only">Sound</span>
          <select
            value={slot}
            onChange={(e) => useEditor.getState().set({ selectedSlot: Number(e.target.value) })}
            data-testid="mix-slot"
          >
            {[...spec.sounds.keys(), ...(lanes ?? [])].map((i) => (
              <option key={i} value={i}>
                {i < 16
                  ? `Pad ${i + 1}: ${spec.sounds[i].name}`
                  : isAtmosPad(i)
                    ? `Atmos ${i - FIRST_ATMOS + 1}: ${spec.sounds[i].name}`
                    : `${ROLE_LABEL[INSTRUMENT_ROLES[laneSlot(i) - 16]]}${i >= NUM_SOUNDS ? " +" : ""}: ${loopSound(spec, variants, i)?.name ?? ""}`}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="knobs">
        {knob("volume", "Volume", pct)}
        {knob("pan", "Pan", pan, { bipolar: true })}
        {knob("cutoff", "Cutoff", hz, { log: true })}
        {knob("reverb_send", "Reverb", pct)}
        {knob("delay_send", "Delay", pct)}
      </div>
      {/E-Guitar/.test(loopSound(spec, variants, slot)?.name ?? "") && (
        <p className="muted small insert-tip" data-testid="guitar-tip">
          The guitars bring their own amp: Clean (neck + middle pickups, a clean amp), Drive (bridge pickup, overdrive)
          and Lead (neck pickup, high gain) — pick one in Sounds. For colour on top: a Band-pass filter for a wah-like
          voice, a High-pass for a thinner rhythm sound.
        </p>
      )}
      <div className="inserts" aria-label="Insert effects">
        <div className="insert">
          <label>
            <span className="insert-label">Drive</span>
            <select
              value={ins.drive}
              onChange={(e) => setInserts({ drive: e.target.value as DriveKind }, true)}
              title={DRIVE_KINDS.find((d) => d.value === ins.drive)?.hint}
              data-testid="insert-drive"
            >
              {DRIVE_KINDS.map((d) => (
                <option key={d.value} value={d.value} title={d.hint}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          {ins.drive !== "off" && amountKnob("drive_amount", "Amount")}
        </div>
        <div className="insert">
          <label>
            <span className="insert-label">Filter</span>
            <select
              value={ins.filter}
              onChange={(e) => setInserts({ filter: e.target.value as InsertFilterKind }, true)}
              title={INSERT_FILTER_KINDS.find((d) => d.value === ins.filter)?.hint}
              data-testid="insert-filter"
            >
              {INSERT_FILTER_KINDS.map((d) => (
                <option key={d.value} value={d.value} title={d.hint}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          {ins.filter !== "off" && amountKnob("filter_amount", ins.filter === "wobble" ? "Depth" : "Freq")}
        </div>
      </div>
    </section>
  );
}
